<template>
  <div id="app">
    <div class="top-navbar">
      <div class="navbar-left">
        <button v-if="isAuthenticated" class="toggle-button" @click="toggleSidebar">
          <i class="fas fa-bars"></i>
        </button>
        <div class="app-title">
          <div class="main-title">Container Inspection and Tracking System</div>
          <div class="sub-title">by FiSYS Technology</div>
        </div>
      </div>
      <div class="navbar-right">
        <div class="navigation-button-custom dropdown" @mouseenter="showProfileMenu = true" @mouseleave="showProfileMenu = false">
          <button v-if="isAuthenticated" class="profile-button">
            <i class="fas fa-user"></i>
          </button>
          <div class="app-title">
            <div class="main-title">Lardner &amp; North</div>
            <div class="sub-title" v-if="isAuthenticated" style="font-size: x-small;">{{username}}</div>
          </div>
          <div v-if="showProfileMenu" class="profile-dropdown" ref="profileDropdown">
            <button @click="openChangePasswordModal">Change Password</button>
          </div>
        </div>
        <div class="profile-menu" v-if="isAuthenticated">
          <button @click="logoutUser" class="logout-button">
            <i class="fas fa-sign-out-alt"></i>
          </button>
        </div>
      </div>
    </div>

    <div class="main-container">
      <!-- Dynamic Sidebar Component -->
      <DynamicSidebar
        v-if="isAuthenticated"
        :menuData="menuItems"
        @menuItemSelected="handleMenuItemSelected"
        class="sidebar"
        :class="{ 'sidebar-closed': !isSidebarOpen }"
      />

      <div class="main-content">
        <router-view></router-view>
      </div>
    </div>

    <!-- Change Password Modal -->
    <ChangePasswordModal 
      :isOpen="showChangePasswordModal"
      @close="showChangePasswordModal = false"
      @passwordChangeSuccess="handlePasswordChangeSuccess"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import DynamicSidebar from '@/components/DynamicSidebar.vue';
import ChangePasswordModal from '@/components/ChangePasswordModal.vue';
import axios from 'axios';

export default {
  name: 'App',
  components: {
    DynamicSidebar,
    ChangePasswordModal,
  },
  data() {
    return {
      isSidebarOpen: true,
      menuItems: [],
      isAuthenticated: false,
      showProfileMenu: false,
      showChangePasswordModal: false,
    };
  },
  computed: {
    ...mapGetters(['user']),
    username() {
      return this.user.name;
    },
  },
  created() {
    this.checkAuthentication();
  },
  mounted() {
    document.addEventListener('click', this.closeProfileMenuOutside);
  },
  unmounted() {
    document.removeEventListener('click', this.closeProfileMenuOutside);
  },
  methods: {
    ...mapActions(['savePermissions', 'logout']),
    async checkAuthentication() {
      const token = localStorage.getItem('access_token');
      if (token != null) {
        this.isAuthenticated = !!token;
        const decodedToken = this.decodeAccessToken(token);
        const exp = decodedToken.payload.exp;
        if (Date.now() >= exp * 1000) {
          this.logoutUser();
        } else {
          if (this.isAuthenticated) {
            await this.fetchMenuItems();
          }
        }
      }
    },
    decodeAccessToken(token) {
      const parts = token.split('.');
      if (parts.length !== 3) {
        throw new Error('Invalid token format');
      }
      return {
        header: JSON.parse(atob(parts[0])),
        payload: JSON.parse(atob(parts[1])),
        signature: parts[2],
      };
    },
    async fetchMenuItems() {
      try {
        const token = localStorage.getItem('access_token');
        const config = {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
            'accept': 'application/json',
            "Authorization": `Bearer ${token}`,
          },
        };
        const response = await axios.get('/menu', config);
        //console.log(response.data[2]);
        this.menuItems = response.data[2];
        // persist menu items so other components can read permissions
        try {
          localStorage.setItem('menuItems', JSON.stringify(response.data[2]));
          // notify other components in same window that menu items have been updated
          try {
            const ev = new CustomEvent('menuItemsUpdated', { detail: response.data[2] });
            window.dispatchEvent(ev);
          } catch (e) {
            // older browsers fallback: set a timestamp flag (components can still poll or read localStorage)
            try {
              localStorage.setItem('menuItemsUpdatedAt', String(Date.now()));
            } catch (err) {
              console.warn('Failed to write menuItemsUpdatedAt to localStorage', err);
            }
          }
        } catch (e) {
          console.warn('Could not persist menuItems to localStorage', e);
        }
      } catch (error) {
        console.error('Error fetching menu items:', error);
      }
    },
    handleMenuItemSelected(menuItem) {
      // Save permission in store and localStorage
      this.savePermissions(menuItem);
      localStorage.setItem('permissions', JSON.stringify(menuItem));
      
      // Close sidebar on mobile
      if (window.innerWidth < 768) {
        this.isSidebarOpen = false;
      }
    },
    logoutUser() {
      this.logout();
    },
    toggleSidebar() {
      this.isSidebarOpen = !this.isSidebarOpen;
    },
    toggleProfileMenu() {
      this.showProfileMenu = !this.showProfileMenu;
    },
    openChangePasswordModal() {
      this.showProfileMenu = false;
      this.showChangePasswordModal = true;
    },
    closeProfileMenuOutside(event) {
      const profileDropdown = this.$refs.profileDropdown;
      if (profileDropdown && !profileDropdown.contains(event.target) && 
          !event.target.closest('.navigation-button-custom')) {
        this.showProfileMenu = false;
      }
    },
    handlePasswordChangeSuccess() {
      // Clear auth data and call logout function
      localStorage.removeItem('access_token');
      localStorage.removeItem('menuItems');
      localStorage.removeItem('permissions');
      
      // Call logout action from Vuex
      this.logout();
      
      // Redirect to login
      this.$router.push('/login');
    },
  },
};
</script>


<style lang="scss">
@import url('https://fonts.googleapis.com/css2?family=Montserrat:ital,wght@0,100..900;1,100..900&display=swap');
$primary-color: #8697C4;
$secondary-color: #f0f0f0;
$text-color: #333;
$sidebar-bg: #f5f5f5;
$sidebar-text-color: #333;
$sidebar-hover-bg: #e9e9e9;
$sidebar-hover-text-color: #333;
$sidebar-width: 200px;
$mobile-breakpoint: 768px; // Adjust this value based on your design

body {
  margin: 0px;
  background-color: #f0f2f5;
  overflow-y: hidden;
  //font-family: 'Roboto', sans-serif;
  //font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-family: 'Montserrat', sans-serif;
  font-weight: 500;
  font-optical-sizing: auto;
  font-style: normal;
}

#app {
  display: flex;
  flex-direction: column;
  height: 100%;

}

h1{
  margin: 0px;
}

.dropdown {
  position: relative;
  display: inline-block;
}

.navigation-button-custom {
  /*position: absolute;
    top: 0;
    z-index: 9999;
    right: 50px;*/
  margin-top: -20px;
  border-radius: 0 0 10px 10px;
  padding: 9px 18px 9px 19px;
  background-color: #3D52A0;
  display: flex;
  margin-right: 20px;
  box-shadow: 2px 2px #3D52DB;
  //box-shadow: 5px 10px #888888;
}

.profile-button {
  background-color: transparent;
  border: none;
  color: white;
  cursor: pointer;
  font-size: 20px;

  &:hover {
    color: darken($primary-color, 10%);
  }
}

.profile-dropdown {
  position: absolute;
  top: 55px;
  right: 0;
  background-color: white;
  color: $text-color;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  border-radius: 0px 0px 5px 5px;
  overflow: hidden;
  z-index: 1001;

  button {
    width: 100%;
    padding: 10px;
    border: none;
    background-color: white;
    color: $text-color;
    cursor: pointer;
    text-align: left;

    &:hover {
      background-color: $secondary-color;
    }
  }
}

.top-navbar {
  background-color: #1a1d23;
  color: white;
  padding: 5px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0px;

  .navbar-left {
    display: flex;
    align-items: center;

    .toggle-button {
      background-color: transparent;
      border: none;
      color: white;
      cursor: pointer;
      font-size: 24px;
      margin-right: 10px;
    }

    .app-title {
      .main-title {
        font-size: 18px;
        font-weight: bold;
        margin-left: 10px;
      }

      .sub-title {
        font-size: 14px;
        font-weight: normal;
        margin-left: 10px;
      }
    }
  }

  .navbar-right {
    display: flex;
    align-items: center;

    .profile-menu {
      position: relative;
      display: flex;
      align-items: center;

      .logout-button {
        background-color: transparent;
        border: none;
        color: #fff;
        cursor: pointer;
        font-size: 20px;
        padding: 5px 10px;
        margin-right: 25px;

        &:hover {
          color: darken($primary-color, 10%);
        }
      }
    }
  }
}

.main-container {
  display: flex;
  height: calc(100vh - 60px); // Subtract the height of the top navbar
}

.sidebar {
  width: 200px;
  height: 100%;
  background-color: #1a1d23;
  overflow-y: auto;
  overflow-x: hidden;
  border-right: 1px solid rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  z-index: 1000;

  &.sidebar-closed {
    width: 0;
    overflow: hidden;
    border-right: none;
  }
}

.main-content {
  flex: 1;
  padding: 0px;
  margin: 0px;
  transition: filter 0.3s ease;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

.main {
  background-color: #f0f2f5;
  border-radius: 0px;
  padding: 7px;
}

.body-content {
  padding: 5px;
  //font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  font-size: 16px;
  color: #333;
  border:1px solid #3D52A0;
  border-radius: 10px;
  background-color: #fff;
  margin-bottom: 2px;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header {
    margin-bottom: 5px;
}

.page-header h1 {
    margin: 0;
    font-size: 1.8rem;
    color: #333;
    border-bottom: 2px solid #17a2b8;
    padding-bottom: 10px;
}
.title {
  display: flex;
  font-size: 30px;
  font-weight: 600;
  color: #333;
  margin-top: 0px;
  margin-bottom: 0px;
}

.export-options {
  display: block;
  top: 20%;
  right: 1%;
}

.export-options li {
  color: black;
  padding: 12px 16px;
  text-decoration: none;
  display: block;
  border-radius: 10px;
  cursor: pointer;
}

.export-options li:hover {
  background-color: #ddd;
}

.table-container {
  max-height: 500px;
  overflow: auto;
  min-height: 320px;  

  padding: 5px;
  border:1px solid #3D52A0;
  border-radius: 10px;
  background-color: #fff;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.tooltip {
  visibility: visible;
  background-color: #555;
  color: #fff;
  text-align: center;
  border-radius: 5px;
  padding: 5px;
  position: absolute;
  z-index: 1;
  bottom: 125%;
  left: 50%;
  margin-left: -60px;
  opacity: 0;
  transition: opacity 0.3s;
}

.tooltip-container:hover .tooltip {
  opacity: 1;
}

// Additional styles for grid, buttons, tables, etc. remain unchanged
.grid-design {
  padding: 0px 20px 0px 20px;
  font-size: 14px;
  color: #333;
}

tr:nth-child(even) {
  background-color: #f5f5f5;
}

.controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.search-container {
  position: relative;
}

.search-container input {
  padding: 8px 30px 8px 10px;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 14px;
}

.error-message {
  color: #d9534f;
  /* Bootstrap's danger color */
  font-size: 12px;
  margin-bottom: 4px;
  /* Space between the error message and input field */
  padding: 4px;
  border: 1px solid #d9534f;
  border-radius: 4px;
  background-color: #f9d6d5;
  /* Light red background */
  display: inline-block;
}

.search-icon {
  position: absolute;
  top: 50%;
  right: 10px;
  transform: translateY(-50%);
  color: #666;
  cursor: pointer;
}

.actions {
  display: flex;
  align-items: center;
}

.add-button {
  background-color: rgb(50, 89, 187);
  color: white;
  border: none;
  padding: 8px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 50%;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  position: relative;
}

.add-button:hover {
  background-color: rgb(61, 146, 231);
}

.export-container {
  position: relative;
  /* Add this */
}

.export-button {
  background-color: rgb(50, 89, 187);
  color: white;
  border: none;
  padding: 8px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 50%;
  transition: background-color 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  position: relative;
  height: 32px;
  margin-left: 10px;
  /* Space between buttons */
}

.export-button:hover {
  background-color: rgb(61, 146, 231);
}

.export-options {
  display: block;
  position: absolute;
  background-color: #f1f1f1;
  min-width: 160px;
  box-shadow: 0px 8px 16px 0px rgba(0, 0, 0, 0.2);
  z-index: 1;
  padding: 0;
  margin: 0;
  list-style-type: none;
  text-align: left;
  top: 100%;
  right: 0;
  border-radius: 10px;
}

.export-button:hover .export-options {
  display: block;
}

.table-design {
  width: 100%;
  border-collapse: collapse;
}

.table-design th,
.table-design td {
  /*min-width: 60px;*/
  padding: 0px;
  text-align: left;
  border-bottom: 1px solid #ddd;
}

.table-design th {
  font-weight: bold;
}

.table-design th .filter-icon {
  cursor: pointer;
  margin-left: 5px;
}

.edit-button,
.save-button,
.cancel-button {
  background: none;
  border: none;
  color: #007bff;
  cursor: pointer;
  font-size: 18px;
}

.save-button {
  color: green;
}

.cancel-button {
  color: red;
}


.bottom-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 20px;
}

.rows-per-table {
  display: flex;
  align-items: center;
}

.rows-per-table button {
  width: 35px;
  height: 35px;
  border: none;
  background-color: #f2f2f2;
  color: #333333;
  font-size: 14px;
  border-radius: 50%;
  margin-left: 10px;
  cursor: pointer;
}

.rows-per-table button.active {
  background-color: rgb(50, 89, 187);
  color: white;
}

.pagination {
  display: flex;
  justify-content: flex-end;
  font-size: 14px;
}

.pagination button {
  width: 35px;
  height: 35px;
  border: none;
  background-color: #f2f2f2;
  color: #333;
  font-size: 15px;
  border-radius: 50%;
  margin-left: 10px;
  cursor: pointer;
}

.pagination button.active {
  background-color: rgb(50, 89, 187);
  color: white;
}

.field-50 {
  width: 50px
}

.field-60 {
  width: 60px
}

.field-70 {
  width: 70px
}

.field-80 {
  width: 80px
}

.field-90 {
  width: 90px
}

.field-100 {
  width: 100px
}

.field-110 {
  width: 110px
}

.field-120 {
  width: 120px
}

.field-150 {
  width: 150px
}

input,
select {
  padding: 5px 2px;
  font-size: 14px;
  /*margin: 5px 0;*/
  border: 1px solid #ccc;
  border-radius: 5px;
}

/* Ensure v-select placeholder is visible */
.v-select .vs__placeholder {
  /* color: #aaa;*/
  /* Change placeholder color if needed */
  overflow: visible;
  /* Ensure placeholder is visible if content overflows */
}

.v-select {
  /* color: #aaa;*/
  /* Change placeholder color if needed */
  overflow: visible;
  /* Ensure placeholder is visible if content overflows */
  background-color: #fff;
  flex-wrap: nowrap;
}

.vs__search {
  padding: 0px;
}

.vs__selected-options {
  flex-wrap: nowrap;

}



.checkbox-pill {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    margin: 0;
    padding: 5px 3px;
    border: 1px solid #1976D2;
    border-radius: 6px;
    background-color: #E3F2FD;
    color: #0D47A1;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    user-select: none;
}

.checkbox-pill--checked {
    background-color: #1976D2;
    border-color: #0D47A1;
    color: #FFFFFF;
    box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
}

.checkbox-input {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    pointer-events: none;
}

.checkbox-box {
    position: relative;
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid #1976D2;
    border-radius: 4px;
    background-color: #FFFFFF;
    transition: all 0.2s ease;
    flex-shrink: 0;
}

.checkbox-pill--checked .checkbox-box {
    background-color: #FFFFFF;
    border-color: #FFFFFF;
}

.checkbox-pill--checked .checkbox-box::after {
    content: "";
    position: absolute;
    left: 5px;
    top: 1px;
    width: 5px;
    height: 10px;
    border: solid #1976D2;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
}
</style>